import { Button, InputNumber, Select } from 'antd';
import { MinusOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from 'react';
import { getIndustryChoices } from 'api/companies';
import { fetchAccessibleParents } from 'api/parents';
import { fetchMerchantsList } from 'api/merchants';
import { getData } from 'utils';
import { CONDITION_FIELDS, CONDITION_OPERATORS } from '../constants';

const FIELD_OPTIONS = CONDITION_FIELDS.map((f) => ({ value: f.value, label: f.label }));

function ConditionBuilder({ condition, index, onChange, onRemove }) {
  const { field, operator, value } = condition;
  const fieldDef = CONDITION_FIELDS.find((f) => f.value === field) || {};
  const uiType = fieldDef.uiType || 'operator_value';

  const [industryChoices, setIndustryChoices] = useState([]);
  const [parents, setParents] = useState([]);
  const [merchants, setMerchants] = useState([]);

  useEffect(() => {
    if (field === 'industry') {
      getIndustryChoices()
        .then((res) => {
          const data = getData(res)?.data ?? getData(res);
          const choices = data?.choices ?? [];
          setIndustryChoices(Array.isArray(choices) ? choices : []);
        })
        .catch(() => setIndustryChoices([]));
    }
  }, [field]);

  useEffect(() => {
    if (field === 'ordered_from_location') {
      fetchAccessibleParents()
        .then((res) => {
          const data = getData(res)?.data ?? getData(res) ?? res;
          const list = data?.parents ?? [];
          setParents(Array.isArray(list) ? list : []);
        })
        .catch(() => setParents([]));
    }
  }, [field]);

  useEffect(() => {
    if (field === 'orders_from_restaurant') {
      fetchMerchantsList({ page_size: 500 })
        .then((res) => {
          const raw = getData(res)?.data ?? getData(res) ?? res;
          const results = raw?.results ?? [];
          setMerchants(Array.isArray(results) ? results : []);
        })
        .catch(() => setMerchants([]));
    }
  }, [field]);

  const handleFieldChange = useCallback(
    (v) => {
      const newCond = { field: v };
      if (v === 'total_spent' || v === 'orders_count') {
        newCond.operator = 'gt';
        newCond.value = v === 'total_spent' ? 0 : 1;
      } else if (v === 'last_order_days_ago') {
        newCond.value = 30;
      } else if (v === 'industry' || v === 'ordered_from_location' || v === 'orders_from_restaurant') {
        newCond.value = [];
      }
      onChange(newCond);
    },
    [onChange],
  );

  const isCurrency = field === 'total_spent';

  if (uiType === 'operator_value') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
        <Select
          placeholder="Select condition"
          value={field}
          onChange={handleFieldChange}
          style={{ minWidth: 180 }}
          options={FIELD_OPTIONS}
        />
        <Select
          placeholder="Operator"
          value={operator}
          onChange={(v) => onChange({ ...condition, operator: v })}
          style={{ minWidth: 120 }}
          options={CONDITION_OPERATORS}
        />
        <InputNumber
          placeholder={isCurrency ? 'Amount' : 'Value'}
          value={value}
          onChange={(v) => onChange({ ...condition, value: v ?? 0 })}
          style={{ width: 120 }}
          min={0}
          precision={isCurrency ? 2 : 0}
          addonBefore={isCurrency ? '$' : undefined}
        />
        <Button type="text" icon={<MinusOutlined />} onClick={onRemove} />
      </div>
    );
  }

  if (uiType === 'days_only') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
        <Select
          placeholder="Select condition"
          value={field}
          onChange={handleFieldChange}
          style={{ minWidth: 180 }}
          options={FIELD_OPTIONS}
        />
        <InputNumber
          value={value ?? 30}
          onChange={(v) => onChange({ ...condition, value: v ?? 30 })}
          min={1}
          max={365}
          style={{ width: 80 }}
        />
        <Button type="text" icon={<MinusOutlined />} onClick={onRemove} />
      </div>
    );
  }

  if (uiType === 'multiselect_industry') {
    const options = industryChoices.map((c) => ({ value: c, label: c }));
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
        <Select
          placeholder="Select condition"
          value={field}
          onChange={handleFieldChange}
          style={{ minWidth: 180 }}
          options={FIELD_OPTIONS}
        />
        <Select
          mode="multiple"
          placeholder="Select industries"
          value={Array.isArray(value) ? value : []}
          onChange={(v) => onChange({ ...condition, value: v ?? [] })}
          style={{ minWidth: 200, flex: 1 }}
          options={options}
          showSearch
          filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        <Button type="text" icon={<MinusOutlined />} onClick={onRemove} />
      </div>
    );
  }

  if (uiType === 'multiselect_location') {
    const options = parents.map((p) => ({ value: p.id, label: p.title }));
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
        <Select
          placeholder="Select condition"
          value={field}
          onChange={handleFieldChange}
          style={{ minWidth: 180 }}
          options={FIELD_OPTIONS}
        />
        <Select
          mode="multiple"
          placeholder="Select locations"
          value={Array.isArray(value) ? value : []}
          onChange={(v) => onChange({ ...condition, value: v ?? [] })}
          style={{ minWidth: 200, flex: 1 }}
          options={options}
          showSearch
          filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        <Button type="text" icon={<MinusOutlined />} onClick={onRemove} />
      </div>
    );
  }

  if (uiType === 'multiselect_restaurant') {
    const options = merchants.map((m) => ({ value: m.id, label: m.name }));
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap' }}>
        <Select
          placeholder="Select condition"
          value={field}
          onChange={handleFieldChange}
          style={{ minWidth: 180 }}
          options={FIELD_OPTIONS}
        />
        <Select
          mode="multiple"
          placeholder="Select restaurants"
          value={Array.isArray(value) ? value : []}
          onChange={(v) => onChange({ ...condition, value: v ?? [] })}
          style={{ minWidth: 200, flex: 1 }}
          options={options}
          showSearch
          filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        <Button type="text" icon={<MinusOutlined />} onClick={onRemove} />
      </div>
    );
  }

  return null;
}

export default ConditionBuilder;
